/**
 * Summary funnel reads on catalog-service (catalogServiceClient). `getFunnelSummary` is the one
 * call allowed to answer "unavailable" instead of throwing: the backend route is new and may not
 * be deployed yet (docs/run-summary-funnel.md §10 step B) — a 404/501 means "render today's
 * Summary unchanged", not an error state.
 */
import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';

import {
  IMAGE_CHECK_FOLD_TARGET,
  IMAGE_CHECK_STAGE,
  PUBLISH_FOLD_TARGET,
  PUBLISH_STAGE,
} from '../constants/hidden-stages';
import {
  foldImageCheckDetail,
  foldImageCheckReasons,
  foldPublishDetail,
  foldPublishReasons,
  foldPublishStages,
  foldPublishTimeline,
  hideImageCheckStage,
} from '../mappers/run-funnel.mapper';
import type { CatalogEnvelope } from '../types/catalog-service.api.types';
import type {
  FunnelComparison,
  FunnelProductMatrix,
  FunnelProductMatrixParams,
  FunnelPublish,
  FunnelStageDetail,
  FunnelStageReasons,
  FunnelSummary,
  FunnelTimeline,
  FunnelVerify,
} from '../types/run-funnel.api.types';
import { toSearchParams, unwrap } from '../utils/envelope';

/** `getFunnelSummary`'s answer: either the summary, or a signal to fall back to the old Summary. */
export type FunnelSummaryResult = { kind: 'ok'; summary: FunnelSummary } | { kind: 'unavailable' };

/**
 * ① The run's verdict, cards, funnel line and waterfall — catalog-service's own DB only (§7).
 * A 404 (route doesn't exist on this deploy) or 501 (not implemented yet) is read as
 * "unavailable", not an error: the caller renders the existing `RunProgressCard` instead.
 */
export async function getFunnelSummary(runRef: string): Promise<FunnelSummaryResult> {
  try {
    const body = await catalogServiceClient
      .get(endpoints.catalogService.operations.funnelSummary(runRef))
      .json<CatalogEnvelope<FunnelSummary>>();
    // Image check is hidden from the UI (its losses fold into Feed intake), and Publish folds
    // into Approval as one "Approve & publish" stage — both in the mapper, applied once here.
    return { kind: 'ok', summary: foldPublishStages(hideImageCheckStage(unwrap(body))) };
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return { kind: 'unavailable' };
    }
    throw err;
  }
}

/** ② One stage's reasons, raw from catalog-service (no Image check fold). */
async function readFunnelStageReasons(
  runRef: string,
  stage: string,
  attempt?: number | null,
): Promise<FunnelStageReasons> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.funnelStageReasons(runRef, stage), {
      searchParams: attempt != null ? { attempt } : {},
    })
    .json<CatalogEnvelope<FunnelStageReasons>>();
  return unwrap(body);
}

/** ② A folded-in stage's reasons (Image check for Feed intake, Publish for Approval) — `null`
 *  when that stage isn't readable (a flow without it answers 404), so a missing folded stage
 *  never breaks the visible stage's read. */
async function readFoldedReasons(
  runRef: string,
  stage: string,
): Promise<FunnelStageReasons | null> {
  try {
    return await readFunnelStageReasons(runRef, stage);
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 404) return null;
    throw err;
  }
}

/**
 * ② One stage's drop/fail reasons for the detail panel's Why tab, optionally one attempt. Feed
 * intake also carries the hidden Image check's reasons (latest attempt), labelled "Images · …";
 * Approval (the combined Approve & publish stage) also carries Publish's, "At publish: …".
 */
export async function getFunnelStageReasons(
  runRef: string,
  stage: string,
  attempt?: number | null,
): Promise<FunnelStageReasons> {
  if (stage === IMAGE_CHECK_FOLD_TARGET) {
    const [target, hidden] = await Promise.all([
      readFunnelStageReasons(runRef, stage, attempt),
      readFoldedReasons(runRef, IMAGE_CHECK_STAGE),
    ]);
    return foldImageCheckReasons(target, hidden);
  }
  if (stage === PUBLISH_FOLD_TARGET) {
    const [approval, delivery] = await Promise.all([
      readFunnelStageReasons(runRef, stage, attempt),
      readFoldedReasons(runRef, PUBLISH_STAGE),
    ]);
    return foldPublishReasons(approval, delivery);
  }
  return readFunnelStageReasons(runRef, stage, attempt);
}

/** ④ Publish/live counts and rejections — lazy on panel open, cached. */
export async function getFunnelPublish(runRef: string): Promise<FunnelPublish> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.funnelPublish(runRef))
    .json<CatalogEnvelope<FunnelPublish>>();
  return unwrap(body);
}

/** ⑤ Stored-vs-live cross-check — lazy on panel open, cached. */
export async function getFunnelVerify(runRef: string): Promise<FunnelVerify> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.funnelVerify(runRef))
    .json<CatalogEnvelope<FunnelVerify>>();
  return unwrap(body);
}

/** ⑥ "Vs last batch" — the previous comparable run's line (needs CS-5; hidden until then). */
export async function getFunnelComparison(runRef: string): Promise<FunnelComparison> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.funnelComparison(runRef))
    .json<CatalogEnvelope<FunnelComparison>>();
  return unwrap(body);
}

/**
 * ⑦ One stage's full breakdown/reasons/markets/gate for the stage page — built in parallel with
 * this UI (funnel-v2-contract.md) and not deployed everywhere yet. A 404/501 degrades to `null`
 * ("details not available yet") instead of throwing, same contract as ①'s `getFunnelSummary`, so
 * the stage page can always fall back to what ①/② already gave it.
 */
export async function getFunnelStageDetail(
  runRef: string,
  stage: string,
  attempt?: number | null,
): Promise<FunnelStageDetail | null> {
  if (stage === IMAGE_CHECK_FOLD_TARGET) {
    // Feed intake absorbs the hidden Image check (latest attempt) — same conservation fold as ①.
    const [target, hidden] = await Promise.all([
      readFunnelStageDetail(runRef, stage, attempt),
      readFunnelStageDetail(runRef, IMAGE_CHECK_STAGE, null),
    ]);
    return foldImageCheckDetail(target, hidden);
  }
  if (stage === PUBLISH_FOLD_TARGET) {
    // Approve & publish: Approval absorbs Publish (latest attempt) — same fold as ①.
    const [approval, delivery] = await Promise.all([
      readFunnelStageDetail(runRef, stage, attempt),
      readFunnelStageDetail(runRef, PUBLISH_STAGE, null),
    ]);
    return foldPublishDetail(approval, delivery);
  }
  return readFunnelStageDetail(runRef, stage, attempt);
}

/** ⑦ One stage's detail, raw from catalog-service — 404/501 → `null` (not deployed / no stage). */
async function readFunnelStageDetail(
  runRef: string,
  stage: string,
  attempt?: number | null,
): Promise<FunnelStageDetail | null> {
  try {
    const body = await catalogServiceClient
      .get(endpoints.catalogService.operations.funnelStageDetail(runRef, stage), {
        searchParams: toSearchParams({ attempt }),
      })
      .json<CatalogEnvelope<FunnelStageDetail>>();
    return unwrap(body);
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) return null;
    throw err;
  }
}

/**
 * ⑧ The run's (or one stage's) event timeline — same "not deployed yet" degrade as ⑦: a 404/501
 * becomes `null` so the Timeline tab can fall back to the existing stage-attempts read. Approval
 * (the combined Approve & publish stage) merges Publish's events in, oldest first.
 */
export async function getFunnelTimeline(
  runRef: string,
  stage?: string | null,
  limit?: number,
): Promise<FunnelTimeline | null> {
  if (stage !== PUBLISH_FOLD_TARGET) return readFunnelTimeline(runRef, stage, limit);
  const [approval, delivery] = await Promise.all([
    readFunnelTimeline(runRef, stage, limit),
    readFunnelTimeline(runRef, PUBLISH_STAGE, limit),
  ]);
  return foldPublishTimeline(approval, delivery);
}

/** ⑧ One timeline read, raw from catalog-service — 404/501 → `null` (not deployed). */
async function readFunnelTimeline(
  runRef: string,
  stage?: string | null,
  limit?: number,
): Promise<FunnelTimeline | null> {
  try {
    const body = await catalogServiceClient
      .get(endpoints.catalogService.operations.funnelTimeline(runRef), {
        searchParams: toSearchParams({ stage, limit }),
      })
      .json<CatalogEnvelope<FunnelTimeline>>();
    return unwrap(body);
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) return null;
    throw err;
  }
}

/**
 * ⑨ The Products matrix (the mix, design "G") — one row per catalog product (or per item that
 * never became one), its outcome at every catalog-owned stage. Built in parallel with this UI
 * (funnel-v2-contract.md); a 404/501 degrades to `null` ("matrix not available yet") so the
 * Products section can fall back to the stage's existing products-source links, same contract
 * as ⑦/⑧.
 */
export async function getFunnelProducts(
  runRef: string,
  params: FunnelProductMatrixParams = {},
): Promise<FunnelProductMatrix | null> {
  try {
    const searchParams = new URLSearchParams(
      Object.entries(
        toSearchParams({
          stage: params.stage,
          outcome: params.outcome,
          q: params.q,
          page: params.page,
          size: params.size,
        }),
      ).map(([key, value]) => [key, String(value)]),
    );
    // ⑨ declares `productIds: list[UUID]` — one repeated key per id. A comma-joined value is a
    // single non-UUID string and the API answers 422 (dev logs, 2026-10-16).
    for (const id of params.productIds ?? []) searchParams.append('productIds', id);
    const body = await catalogServiceClient
      .get(endpoints.catalogService.operations.funnelProducts(runRef), { searchParams })
      .json<CatalogEnvelope<FunnelProductMatrix>>();
    return unwrap(body);
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) return null;
    throw err;
  }
}
